import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Button, Card, DatePicker, Empty, Form, Input, List, Select, Space, Spin, Tag, Typography, message } from 'antd';
import { TeamOutlined, TrophyOutlined, UserAddOutlined, ReloadOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { API_BASE_URL, getAuthHeaders, parseApiJson } from '../api';
import { useAuth } from '../contexts/AuthContext';

const { Title, Paragraph, Text } = Typography;

interface ClubAthlete {
  athlete_id: number;
  full_name: string;
  birth_date: string | null;
  gender: string | null;
  country_code: string | null;
  account_email: string | null;
  firebase_uid: string | null;
}

interface ClubResult {
  results_key: number;
  athlete_id: number;
  athlete_name: string;
  event_name: string | null;
  competition_name: string | null;
  mark: string | null;
  place: string | null;
  race_date: string | null;
}

interface ClubOption {
  id: number;
  club_name: string;
}

interface MembershipRequest {
  uid: string;
  email: string | null;
  full_name: string | null;
  date_of_birth: string | null;
  membership_status: string | null;
  membership_requested_at: string | null;
  membership_review_note: string | null;
  requested_club_id: number | null;
  subscription_status: string | null;
}

const ClubManagement: React.FC = () => {
  const { user, features } = useAuth();
  const isAdmin = user?.admin === true || features?.admin === true;
  const meClubId = user?.clubId ?? null;
  const [form] = Form.useForm();
  const [creating, setCreating] = useState(false);
  const [clubs, setClubs] = useState<ClubOption[]>([]);
  const [selectedClubId, setSelectedClubId] = useState<number | null>(null);
  const [loadingAthletes, setLoadingAthletes] = useState(false);
  const [loadingResults, setLoadingResults] = useState(false);
  const [loadingRequests, setLoadingRequests] = useState(false);
  const [reviewingUid, setReviewingUid] = useState<string | null>(null);
  const [apiError, setApiError] = useState<string | null>(null);
  const [athletes, setAthletes] = useState<ClubAthlete[]>([]);
  const [results, setResults] = useState<ClubResult[]>([]);
  const [requests, setRequests] = useState<MembershipRequest[]>([]);

  const effectiveClubId = selectedClubId ?? meClubId;

  const clubQuery = useMemo(() => {
    if (effectiveClubId == null) return '';
    if (!isAdmin) return '';
    return `?club_id=${effectiveClubId}`;
  }, [effectiveClubId, isAdmin]);

  const clubBodyExtra = useMemo(() => {
    if (effectiveClubId == null) return {};
    if (!isAdmin) return {};
    return { club_id: effectiveClubId };
  }, [effectiveClubId, isAdmin]);

  useEffect(() => {
    if (!isAdmin) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/public/clubs`);
        const payload = await res.json();
        if (!res.ok) throw new Error(payload.error || 'Failed to load clubs');
        if (cancelled) return;
        const list: ClubOption[] = payload.clubs || [];
        setClubs(list);
        setSelectedClubId((prev) => {
          if (prev != null && list.some((c) => c.id === prev)) return prev;
          if (meClubId != null && list.some((c) => c.id === meClubId)) return meClubId;
          return list[0]?.id ?? null;
        });
      } catch (e: any) {
        if (!cancelled) setApiError(e.message);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isAdmin, meClubId]);

  useEffect(() => {
    if (!isAdmin && meClubId != null) {
      setSelectedClubId(meClubId);
    }
  }, [isAdmin, meClubId]);

  const loadAthletes = useCallback(async () => {
    if (isAdmin && effectiveClubId == null) return;
    setLoadingAthletes(true);
    try {
      const headers = await getAuthHeaders();
      const res = await fetch(`${API_BASE_URL}/api/club/athletes${clubQuery}`, { headers });
      const payload = await parseApiJson<{ error?: string; athletes?: ClubAthlete[] }>(res);
      if (!res.ok) throw new Error(payload.error || 'Failed to load athletes');
      setAthletes(payload.athletes || []);
      setApiError(null);
    } catch (e: any) {
      setApiError(e.message);
    } finally {
      setLoadingAthletes(false);
    }
  }, [clubQuery, effectiveClubId, isAdmin]);

  const loadResults = useCallback(async () => {
    if (isAdmin && effectiveClubId == null) return;
    setLoadingResults(true);
    try {
      const headers = await getAuthHeaders();
      const res = await fetch(`${API_BASE_URL}/api/club/results${clubQuery}`, { headers });
      const payload = await parseApiJson<{ error?: string; results?: ClubResult[] }>(res);
      if (!res.ok) throw new Error(payload.error || 'Failed to load results');
      setResults(payload.results || []);
      setApiError(null);
    } catch (e: any) {
      setApiError(e.message);
    } finally {
      setLoadingResults(false);
    }
  }, [clubQuery, effectiveClubId, isAdmin]);

  const loadMembershipRequests = useCallback(async () => {
    if (isAdmin && effectiveClubId == null) return;
    setLoadingRequests(true);
    try {
      const headers = await getAuthHeaders();
      const res = await fetch(`${API_BASE_URL}/api/club/membership-requests${clubQuery}`, { headers });
      const payload = await parseApiJson<{ error?: string; requests?: MembershipRequest[] }>(res);
      if (!res.ok) throw new Error(payload.error || 'Failed to load membership requests');
      setRequests(payload.requests || []);
      setApiError(null);
    } catch (e: any) {
      setApiError(e.message);
    } finally {
      setLoadingRequests(false);
    }
  }, [clubQuery, effectiveClubId, isAdmin]);

  useEffect(() => {
    loadAthletes();
    loadResults();
    loadMembershipRequests();
  }, [loadAthletes, loadResults, loadMembershipRequests]);

  const handleCreateAthlete = async (values: any) => {
    if (isAdmin && effectiveClubId == null) {
      message.error('Select a club first');
      return;
    }
    setCreating(true);
    try {
      const headers = await getAuthHeaders();
      const res = await fetch(`${API_BASE_URL}/api/club/athlete-accounts`, {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: values.email,
          full_name: values.full_name,
          date_of_birth: values.date_of_birth.format('YYYY-MM-DD'),
          ...clubBodyExtra,
        }),
      });
      const payload = await parseApiJson<{ error?: string; email?: string }>(res);
      if (!res.ok) throw new Error(payload.error || 'Failed to create athlete account');
      message.success(`Created athlete account for ${payload.email}`);
      form.resetFields();
      loadAthletes();
      loadMembershipRequests();
    } catch (e: any) {
      message.error(`Failed: ${e.message}`);
    } finally {
      setCreating(false);
    }
  };

  const reviewMembershipRequest = async (uid: string, action: 'approve' | 'reject') => {
    setReviewingUid(uid);
    try {
      const headers = await getAuthHeaders();
      const res = await fetch(`${API_BASE_URL}/api/club/membership-requests/${uid}/${action}${clubQuery}`, {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify(clubBodyExtra),
      });
      const payload = await res.json();
      if (!res.ok) throw new Error(payload.error || `Failed to ${action} request`);
      message.success(`Membership ${action}d`);
      loadMembershipRequests();
      loadAthletes();
    } catch (e: any) {
      message.error(`Failed: ${e.message}`);
    } finally {
      setReviewingUid(null);
    }
  };

  return (
    <div className="page-container">
      <Title level={2}>Club Management</Title>
      <Paragraph type="secondary">
        Create athlete accounts for your club and view only athletes/results linked to your club.
      </Paragraph>

      {apiError && <Alert type="error" showIcon closable message={apiError} style={{ marginBottom: 16 }} />}

      {isAdmin && (
        <Card title="Club" style={{ marginBottom: 16 }}>
          <Form.Item label="Manage club" style={{ marginBottom: 0 }}>
            <Select
              placeholder="Select a club"
              value={selectedClubId ?? undefined}
              onChange={(id) => setSelectedClubId(id)}
              options={clubs.map((c) => ({ value: c.id, label: c.club_name }))}
              style={{ width: '100%', maxWidth: 400 }}
              showSearch
              optionFilterProp="label"
            />
          </Form.Item>
        </Card>
      )}

      <Card title={<Space><UserAddOutlined /> Create Athlete Account</Space>} style={{ marginBottom: 16 }}>
        <Form form={form} layout="vertical" onFinish={handleCreateAthlete}>
          <Form.Item name="full_name" label="Athlete full name" rules={[{ required: true }]}>
            <Input placeholder="Jane Tan" />
          </Form.Item>
          <Form.Item name="email" label="Athlete email" rules={[{ required: true, type: 'email' }]}>
            <Input placeholder="athlete@example.com" />
          </Form.Item>
          <Form.Item name="date_of_birth" label="Date of birth" rules={[{ required: true }]}>
            <DatePicker style={{ width: '100%' }} />
          </Form.Item>
          <Button type="primary" htmlType="submit" loading={creating}>
            Create athlete account and send invite
          </Button>
        </Form>
      </Card>

      <Card
        title={<Space><TeamOutlined /> Membership Requests</Space>}
        extra={<Button icon={<ReloadOutlined />} onClick={loadMembershipRequests} loading={loadingRequests} size="small" />}
        style={{ marginBottom: 16 }}
      >
        <Spin spinning={loadingRequests}>
          {requests.length === 0 ? (
            <Empty description="No membership requests" image={Empty.PRESENTED_IMAGE_SIMPLE} />
          ) : (
            <List
              dataSource={requests}
              renderItem={(req: MembershipRequest) => (
                <List.Item
                  actions={[
                    <Button
                      key="approve"
                      type="primary"
                      size="small"
                      disabled={req.membership_status !== 'pending'}
                      loading={reviewingUid === req.uid}
                      onClick={() => reviewMembershipRequest(req.uid, 'approve')}
                    >
                      Approve
                    </Button>,
                    <Button
                      key="reject"
                      danger
                      size="small"
                      disabled={req.membership_status !== 'pending'}
                      loading={reviewingUid === req.uid}
                      onClick={() => reviewMembershipRequest(req.uid, 'reject')}
                    >
                      Reject
                    </Button>,
                  ]}
                >
                  <List.Item.Meta
                    title={
                      <Space wrap>
                        <Text strong>{req.full_name || req.email || req.uid}</Text>
                        <Tag color={req.membership_status === 'pending' ? 'gold' : req.membership_status === 'approved' ? 'green' : 'red'}>
                          {req.membership_status || 'unknown'}
                        </Tag>
                      </Space>
                    }
                    description={
                      <Space direction="vertical" size={0}>
                        <Text type="secondary">{req.email || 'No email'}</Text>
                        <Text type="secondary">Subscription: {req.subscription_status || 'unknown'}</Text>
                        <Text type="secondary">
                          Requested: {req.membership_requested_at ? dayjs(req.membership_requested_at).format('YYYY-MM-DD HH:mm') : '—'}
                        </Text>
                      </Space>
                    }
                  />
                </List.Item>
              )}
            />
          )}
        </Spin>
      </Card>

      <Card
        title={<Space><TeamOutlined /> Club Athletes</Space>}
        extra={<Button icon={<ReloadOutlined />} onClick={loadAthletes} loading={loadingAthletes} size="small" />}
        style={{ marginBottom: 16 }}
      >
        <Spin spinning={loadingAthletes}>
          {athletes.length === 0 ? (
            <Empty description="No club athletes linked yet" image={Empty.PRESENTED_IMAGE_SIMPLE} />
          ) : (
            <List
              dataSource={athletes}
              renderItem={(athlete: ClubAthlete) => (
                <List.Item>
                  <List.Item.Meta
                    title={
                      <Space wrap>
                        <Text strong>{athlete.full_name}</Text>
                        <Tag color="blue">ID {athlete.athlete_id}</Tag>
                      </Space>
                    }
                    description={
                      <Space direction="vertical" size={0}>
                        <Text type="secondary">DOB: {athlete.birth_date || '—'}</Text>
                        <Text type="secondary">Account: {athlete.account_email || 'Not linked yet'}</Text>
                      </Space>
                    }
                  />
                </List.Item>
              )}
            />
          )}
        </Spin>
      </Card>

      <Card
        title={<Space><TrophyOutlined /> Club Results</Space>}
        extra={<Button icon={<ReloadOutlined />} onClick={loadResults} loading={loadingResults} size="small" />}
      >
        <Spin spinning={loadingResults}>
          {results.length === 0 ? (
            <Empty description="No results for this club yet" image={Empty.PRESENTED_IMAGE_SIMPLE} />
          ) : (
            <List
              dataSource={results}
              renderItem={(result: ClubResult) => (
                <List.Item>
                  <List.Item.Meta
                    title={
                      <Space wrap>
                        <Text strong>{result.athlete_name}</Text>
                        <Text>{result.event_name || 'Unknown event'}</Text>
                        {result.mark && <Tag>{result.mark}</Tag>}
                      </Space>
                    }
                    description={
                      <Space direction="vertical" size={0}>
                        <Text type="secondary">{result.competition_name || 'Unknown competition'}</Text>
                        <Text type="secondary">
                          {result.race_date ? dayjs(result.race_date).format('YYYY-MM-DD') : 'No race date'}
                        </Text>
                      </Space>
                    }
                  />
                </List.Item>
              )}
            />
          )}
        </Spin>
      </Card>
    </div>
  );
};

export default ClubManagement;
